/* eslint-disable no-console */
import { Client, IMessage } from '@stomp/stompjs';

import { EOptionSymbolSide, ESymbolType, IHorizonSymbolTickRulesGroup } from '@webtrader/core/api/market/symbols';
import { ENotificationOrderStateCentroid, ERejectOrderRequestType } from '@webtrader/core/api/notifications';
import {
  EHorizonExecuteType,
  EHorizonOrderStatus,
  ENotificationTemplate
} from '@webtrader/core/api/notifications/constants';
import { EOrderState, EOrderType, EOrderWebsocketMessageType } from '@webtrader/core/api/order';
import { ETradeFillPolicyValue } from '@webtrader/core/api/trade';
import { ETradingState } from '@webtrader/core/api/user';
import { formatTopicId, formatTopicPath } from '@webtrader/core/format/socket';
import storage, { EStorageKey } from '@webtrader/core/storage';

import { ISocketTopic } from './index';

export enum EMetatraderMessageType {
  ORDER = 'ORDER',
  BALANCE = 'BALANCE',
  NOTIFICATION = 'NOTIFICATION',
  ONE_CLICK = 'ONE_CLICK'
}
export interface IUseMetaTraderTopicParams {
  low: number;
  high: number;
  buy: number;
  sell: number;
}

export interface IMetaTraderNotificationMessageData {
  T: number; // time
}

export interface IMetaTraderOrderMessageData {
  O: number | string; // orderid
  T: EOrderWebsocketMessageType; // type
  C: EOrderType; // cmd - OrderType
  S: string; // symbol
  OP: number; // openPrice
  CP: number; // closePrice
  V: number; // volume
  MT4: number; // metatraderId / accountId
  SL: number; // stopLoss
  TP: number; // takeProfit
  P: number; // profit
  TR: number; // tradeId
  ST: EOrderState; // orderState
  CMT: string; // comment - reason
  HOS: EHorizonOrderStatus; // horizonOrderStatus - enum
  HET: EHorizonExecuteType; // horizonExecuteType
  SS: EOptionSymbolSide; // option symbolSide
  LS: number; // lotSize
  FLS: number; // filledLotSize
  RLS: number; // remainedLotSize
  TIF: ETradeFillPolicyValue; // horizonTimeInForce
  PRI: number; // price
  SP: number; // stopPrice
  AFP: number; // avgFillPrice
  OT: number; // openTime - timestamps
  E: number; // expireTime - timestamps
  TRG: IHorizonSymbolTickRulesGroup[]; // tickRulesGroup (for Horizon only)
  STYP: ESymbolType; // symbolType
  CI: string; // clientOrderId (Horizon)
  OCI: string; // origClientOrderId (Horizon)
  ROE: ERejectOrderRequestType; // orderCancelRequestType
  SEQ: number; // sequence
  CNOS: ENotificationOrderStateCentroid; // centroidNotificationOrderState
  CTIF: ETradeFillPolicyValue; // centroidTimeInForce
  CNT: ENotificationTemplate; // centroidNotificationTemplate
  CSD: number; // centroidSymbolDecimal
}

export interface IMetaTraderBalanceMessageData {
  T: number; // time
  C: EOrderType; // order type
  B: number; // balance
  M: number; // margin
  E: number; // equity
  FM: number; // free margin
  PL: number; // profit or loss
  ML: number; // margin level
  MC: number; // margin call
  MSO: number; // margin stop out
  N: number; // notional
  CM: number; // commission
  S: number; // swaps
  AW: number; // available withdraw
  TS: ETradingState; // trading state
  CD: number; // credit
}
export interface IMetaTraderOrderMessage {
  M: EMetatraderMessageType.ORDER;
  D: IMetaTraderOrderMessageData;
}

export interface IMetaTraderBalanceMessage {
  M: EMetatraderMessageType.BALANCE;
  D: IMetaTraderBalanceMessageData;
}

export interface IMetaTraderNotificationMessage {
  M: EMetatraderMessageType.NOTIFICATION;
  D: IMetaTraderNotificationMessageData;
}

export interface IMetaTraderOneClickMessage {
  M: EMetatraderMessageType.ONE_CLICK;
  D: IMetaTraderNotificationMessageData;
}

export type IMetaTraderMessage =
  | IMetaTraderOrderMessage
  | IMetaTraderBalanceMessage
  | IMetaTraderNotificationMessage
  | IMetaTraderOneClickMessage;

export interface IMetaTraderReceiver {
  (payload: IMetaTraderMessage | undefined): void;
}

interface ISubscribeParams {
  receiver: IMetaTraderReceiver;
}

interface IProps {
  client: Client;
}

class MetaTrader implements ISocketTopic {
  private client: Client;
  private receivers: Record<string, Set<IMetaTraderReceiver> | undefined> = {};

  constructor(props: IProps) {
    const { client } = props;

    this.client = client;
  }

  public subscribe = (params: ISubscribeParams) => {
    const { receiver } = params;

    const { subPath, topicId } = this.mapTopicIds();

    if (!this.client) {
      return;
    }

    if (this.receivers[subPath]) {
      this.receivers[subPath]?.add(receiver);
    } else {
      this.receivers[subPath] = new Set([receiver]);

      try {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: topicId
        });
      } catch (err) {}
    }
  };

  public unsubscribe = (params: ISubscribeParams) => {
    const { receiver } = params;

    const { subPath, topicId } = this.mapTopicIds();

    if (!this.client) {
      return;
    }

    this.receivers[subPath]?.delete(receiver);

    if (this.receivers[subPath]?.size === 0) {
      try {
        this.client.unsubscribe(topicId);
      } catch (err) {}

      this.receivers[subPath] = undefined;
    }
  };

  public reconnect = () => {
    const { receivers } = this;

    if (receivers) {
      Object.keys(receivers).forEach((subPath) => {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: formatTopicId(subPath)
        });
      });
    }
  };

  public clearSubscriptions = () => {
    this.receivers = {};
  };

  private onReceive = (id: string, message: IMessage) => {
    const receivers = this.receivers[id];

    if (!receivers || !message) {
      return;
    }

    const payload = JSON.parse(message.body) as IMetaTraderMessage;

    receivers.forEach((receiver) => receiver(payload));
  };

  private mapTopicIds = () => {
    const tradeSubscription = storage.getItem<string>(EStorageKey.TRADE_SUBSCRIPTION);
    const subPath = `metatrader/${tradeSubscription}`;

    return {
      subPath,
      topicId: formatTopicId(subPath)
    };
  };
}

export default MetaTrader;
